import React, { useState, useEffect } from 'react';
import { ArrowLeft, Key, Check, ExternalLink, AlertCircle, Sparkles, CreditCard, Activity, User, Shield, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import clientAIService from '../services/clientAIService';
import { useAuth } from '../context/AuthContext';
import billingService from '../services/billingService';
import duckdbService from '../services/duckdbService';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, authLoading, usageStatus, billingStatus, billingLoading, sessionUser, firebaseUser, signOut } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [saved, setSaved] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [clearingData, setClearingData] = useState(false);

  // Storage calculation
  const [storageInfo, setStorageInfo] = useState({ files: [], totalBytes: 0 });

  useEffect(() => {
    const existingKey = clientAIService.getApiKey();
    if (existingKey) {
      setApiKey(existingKey);
      setSaved(true);
    }
    loadStorageInfo();
  }, []);

  const loadStorageInfo = async () => {
    try {
      const files = duckdbService.getAllDatasets();
      const totalBytes = files.reduce((sum, file) => sum + (Number(file.file_size_bytes) || 0), 0);
      setStorageInfo({ files, totalBytes });
    } catch (error) {
      console.error('Failed to load storage info:', error);
    }
  };

  const handleSave = () => {
    clientAIService.setApiKey(apiKey.trim());
    setSaved(true);
  };

  const handleClear = () => {
    setApiKey('');
    clientAIService.setApiKey(null);
    setSaved(false);
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const session = await billingService.createPortalSession();
      window.location.href = session.url;
    } catch (error) {
      console.error('Unable to open billing portal:', error);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleClearAllData = async () => {
    if (!window.confirm('Are you sure you want to delete all local data? This cannot be undone.')) {
      return;
    }
    setClearingData(true);
    try {
      await duckdbService.clearAllDatasets();
      await loadStorageInfo();
    } catch (error) {
      console.error('Failed to clear data:', error);
    } finally {
      setClearingData(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const planLabel = sessionUser?.plan_type === 'pro'
    ? 'Pro'
    : sessionUser?.plan_type === 'team'
      ? 'Team'
      : 'Free';

  // Redirect if not authenticated and not loading
  if (!authLoading && !isAuthenticated) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="mx-auto max-w-3xl px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-lg p-2 text-sand-700 hover:bg-sand-100 hover:text-ink transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold">Settings</h1>
            <p className="text-sm text-sand-600">Manage your account and preferences</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Profile Section */}
          <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm">
            <div className="flex items-center gap-3 mb-4">
              <User className="h-5 w-5 text-bill-600" />
              <h2 className="text-lg font-semibold">Profile</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-sand-200">
                <span className="text-sm text-sand-700">Email</span>
                <span className="text-sm text-ink">{firebaseUser?.email || sessionUser?.email || '—'}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-sand-200">
                <span className="text-sm text-sand-700">Plan</span>
                <span className="text-sm text-ink">{planLabel}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-sand-700">Account</span>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>

          {/* Usage & Billing Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="h-5 w-5 text-sage-600" />
                <h3 className="font-semibold">Usage</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-sand-700">Uploads</span>
                    <span className="text-ink">
                      {usageStatus?.uploads_used ?? 0} / {usageStatus?.uploads_limit === null ? '∞' : usageStatus?.uploads_limit ?? '—'}
                    </span>
                  </div>
                  {usageStatus?.uploads_limit && (
                    <div className="h-2 rounded-full bg-sand-200 overflow-hidden">
                      <div
                        className="h-full bg-sage-500 rounded-full transition-all"
                        style={{ width: `${Math.min(100, ((usageStatus?.uploads_used || 0) / usageStatus.uploads_limit) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-sand-700">Queries</span>
                    <span className="text-ink">
                      {usageStatus?.queries_used ?? 0} / {usageStatus?.queries_limit === null ? '∞' : usageStatus?.queries_limit ?? '—'}
                    </span>
                  </div>
                  {usageStatus?.queries_limit && (
                    <div className="h-2 rounded-full bg-sand-200 overflow-hidden">
                      <div
                        className="h-full bg-bill-500 rounded-full transition-all"
                        style={{ width: `${Math.min(100, ((usageStatus?.queries_used || 0) / usageStatus.queries_limit) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-sand-600 mt-2">Resets on the 1st of each month</p>
              </div>
            </div>

            <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-5 w-5 text-bill-600" />
                <h3 className="font-semibold">Billing</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-sand-700">Status</span>
                  <span className="text-ink capitalize">{billingStatus?.subscription_status || 'Free'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-sand-700">Next billing</span>
                  <span className="text-ink">
                    {billingStatus?.current_period_end
                      ? new Date(billingStatus.current_period_end).toLocaleDateString()
                      : '—'}
                  </span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => navigate('/billing')}
                    className="flex-1 rounded-lg border border-[rgba(32,30,29,0.18)] bg-white px-3 py-2 text-sm font-medium text-ink hover:bg-sand-100 transition-colors"
                  >
                    View details
                  </button>
                  {sessionUser?.plan_type !== 'free' && (
                    <button
                      type="button"
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                      className="flex-1 rounded-lg bg-bill-500 px-3 py-2 text-sm font-medium text-white hover:bg-bill-600 disabled:opacity-50 transition-colors shadow-brand-sm"
                    >
                      {portalLoading ? 'Opening...' : 'Manage'}
                    </button>
                  )}
                </div>
                {billingLoading && (
                  <p className="text-xs text-sand-600">Syncing billing status...</p>
                )}
              </div>
            </div>
          </div>

          {/* Storage Section */}
          <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-sage-600" />
                <h3 className="font-semibold">Local Storage</h3>
              </div>
              <span className="text-sm text-sand-700">
                {formatBytes(storageInfo.totalBytes)} used
              </span>
            </div>
            <p className="text-sm text-sand-700 mb-4">
              {storageInfo.files.length} file{storageInfo.files.length !== 1 ? 's' : ''} stored locally in your browser.
              Data never leaves your device unless you explicitly sync to cloud.
            </p>
            <button
              type="button"
              onClick={handleClearAllData}
              disabled={clearingData || storageInfo.files.length === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              {clearingData ? 'Clearing...' : 'Clear all local data'}
            </button>
          </div>

          {/* API Key Section */}
          <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm">
            <div className="flex items-center gap-2 mb-4">
              <Key className="h-5 w-5 text-bill-600" />
              <h3 className="font-semibold">Gemini API Key</h3>
              <span className="text-xs px-2 py-0.5 bg-bill-100 text-bill-700 rounded">Optional</span>
            </div>

            <p className="text-sm text-sand-700 mb-4">
              Add your Google Gemini API key to enable advanced AI-powered analysis directly in your browser.
              Without an API key, basic rule-based SQL generation will be used.
            </p>

            <div className="relative mb-4">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setSaved(false);
                }}
                placeholder="AIzaSy..."
                className="w-full px-4 py-3 bg-sand-100 border border-[rgba(32,30,29,0.12)] rounded-xl text-ink placeholder-sand-500 focus:outline-none focus:border-bill-500 pr-24 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-sand-600 hover:text-sand-800"
              >
                {showKey ? 'Hide' : 'Show'}
              </button>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={!apiKey.trim() || saved}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-bill-500 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bill-600 transition-colors shadow-brand-sm"
              >
                {saved ? (
                  <>
                    <Check className="w-4 h-4" />
                    Saved
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Save API Key
                  </>
                )}
              </button>

              {apiKey && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-4 py-2.5 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* API Key Help */}
          <div className="rounded-2xl border border-bill-200 bg-bill-100 p-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-bill-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm text-bill-800 font-medium">How to get a Gemini API Key</p>
                <ol className="text-sm text-sand-700 list-decimal list-inside space-y-1">
                  <li>Go to Google AI Studio</li>
                  <li>Sign in with your Google account</li>
                  <li>Click "Get API Key" → "Create API key"</li>
                  <li>Copy and paste the key above</li>
                </ol>
                <a
                  href="https://aistudio.google.com/app/apikey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-bill-600 hover:text-bill-700"
                >
                  Get your API key
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          {/* Privacy Note */}
          <p className="text-xs text-sand-600 text-center">
            Your API key is stored locally in your browser and is never sent to our servers.
            All AI requests go directly from your browser to Google's API.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
